import {EPermissao} from '@/core/type/permissao.type';
import {useAuthStore} from '@/stores/auth.store';
import {itemVisivel} from './menu.util';
import type {MenuItemFolha, MenuModulo, MenuSubgrupo} from './menu.type';

const algumItemVisivel = (subgrupos: MenuSubgrupo[]): boolean =>
    subgrupos.some((subgrupo) => subgrupo.itens.some(itemVisivel));

export function getMenuModulos(): MenuModulo[] {
    const {temPermissao} = useAuthStore();

    const criarModulo = (dados: Omit<MenuModulo, 'visible'>): MenuModulo => ({
        ...dados,
        visible: algumItemVisivel(dados.subgrupos)
    });

    const inicio: MenuItemFolha[] = [
        {id: 'home', label: 'Home', icon: 'pi pi-fw pi-home', to: '/', favoritavel: false}
    ];

    const operacao: MenuItemFolha[] = [
        {
            id: 'lista-arranque-preenchimento',
            label: 'Lista de Arranque',
            descricao: 'Preencha a lista de arranque (RLI) para liberar a máquina, etapa por etapa.',
            palavrasChave: ['rli', 'setup', 'liberação'],
            icon: 'pi pi-fw pi-list-check',
            to: '/rli',
            visible: temPermissao(EPermissao.CGR_PRISMA_PREENCHER_LISTA_ARRANQUE)
        },
        {
            id: 'atividades',
            label: 'Atividades',
            descricao: 'Acompanhe os indicadores das atividades em andamento (abre em nova guia).',
            icon: 'pi pi-fw pi-list-check',
            command: () => {
                window.open('https://ucase.com.br/prisma/cliente.html', '_blank', 'noopener,noreferrer');
            },
            visible: temPermissao(EPermissao.CGR_PRISMA_LISTAR_ATIVIDADES)
        }
    ];

    const relatoriosOperacionais: MenuItemFolha[] = [
        {
            id: 'lista-arranque-consulta',
            label: 'Lista de Arranque',
            descricao: 'Consulte as listas de arranque já preenchidas e seus resultados.',
            palavrasChave: ['rli'],
            icon: 'pi pi-fw pi-list',
            to: {name: 'lista-arranque'},
            visible: temPermissao(EPermissao.CGR_PRISMA_LISTA_ARRANQUE_CONSULTAR)
        },
        {
            id: 'diario-bordo',
            label: 'Diário de Bordo',
            descricao: 'Consulte as ocorrências registradas nas máquinas e as ações tomadas.',
            palavrasChave: ['ocorrência'],
            icon: 'pi pi-fw pi-book',
            to: {name: 'diario-bordo'},
            visible: temPermissao(EPermissao.CGR_PRISMA_DIARIO_BORDO_CONSULTAR)
        },
        {
            id: 'apontamentos-rli',
            label: 'Apontamentos RLI',
            descricao: 'Consulte os apontamentos registrados nas inspeções do RLI.',
            palavrasChave: ['inspeção'],
            icon: 'pi pi-fw pi-file-edit',
            to: {name: 'lista-arranque-rli'},
            visible: temPermissao(EPermissao.CGR_PRISMA_APONTAMENTO_RLI_CONSULTAR)
        },
        {
            id: 'apuracao-nota',
            label: 'Apuração Nota',
            descricao: 'Consulte a apuração de impostos por nota fiscal (CFOP, PIS, COFINS) e exporte.',
            palavrasChave: ['imposto', 'icms', 'nf'],
            icon: 'pi pi-fw pi-table',
            to: {name: 'apuracao-nota'},
            visible: temPermissao(EPermissao.CGR_PRISMA_LISTAR_APURACAO)
        }
    ];

    const relatoriosFaturamento: MenuItemFolha[] = [
        {
            id: 'curva-abc',
            label: 'Curva ABC Nota Fiscal',
            descricao: 'Analise a curva ABC do faturamento com gráficos e tabela exportável.',
            palavrasChave: ['nf', 'vendas'],
            icon: 'pi pi-fw pi-chart-line',
            to: {name: 'curva-abc'},
            visible: temPermissao(EPermissao.CGR_PRISMA_CONSULTAR_CURVA_ABC)
        },
        {
            id: 'faturamento-notafiscal',
            label: 'Faturamento Nota Fiscal',
            descricao: 'Acompanhe o faturamento das notas fiscais com indicadores e gráficos.',
            palavrasChave: ['nf', 'vendas'],
            icon: 'pi pi-table pi-chart-line',
            to: {name: 'faturamento-notafiscal'},
            visible: temPermissao(EPermissao.CGR_PRISMA_CONSULTAR_CURVA_ABC)
        }
    ];

    const financeiroLancamentos: MenuItemFolha[] = [
        {
            id: 'lancamento',
            label: 'Lançamentos',
            descricao: 'Consulte e cadastre lançamentos financeiros a pagar e a receber e controle o status.',
            palavrasChave: ['contas a pagar', 'contas a receber', 'título'],
            icon: 'pi pi-fw pi-money-bill',
            to: {name: 'lancamento'},
            visible: temPermissao(EPermissao.CGR_PRISMA_LISTAR_LANCAMENTOS)
        },
        {
            id: 'lancamento-recorrencia',
            label: 'Lançamentos Recorrentes',
            descricao: 'Configure lançamentos que se repetem por periodicidade e gere os do período.',
            palavrasChave: ['recorrente', 'mensal'],
            icon: 'pi pi-fw pi-sync',
            to: {name: 'lancamento-recorrencia'},
            visible: temPermissao(EPermissao.CGR_PRISMA_LISTAR_LANCAMENTOS_RECORRENTES)
        },
        {
            id: 'transferencia',
            label: 'Transferência entre Contas',
            descricao: 'Registre transferências de valores entre contas bancárias.',
            icon: 'pi pi-fw pi-arrow-right-arrow-left',
            to: {name: 'transferencia'},
            visible: temPermissao(EPermissao.CGR_PRISMA_LISTAR_TRANSFERENCIAS)
        },
        {
            id: 'fluxo-caixa',
            label: 'Fluxo de Caixa',
            descricao: 'Acompanhe o que há a receber, a pagar e o resultado previsto por período.',
            palavrasChave: ['entradas', 'saídas'],
            icon: 'pi pi-fw pi-chart-line',
            to: {name: 'fluxo-caixa'},
            visible: temPermissao(EPermissao.CGR_PRISMA_LISTAR_FLUXO_CAIXA)
        }
    ];

    const financeiroExtratos: MenuItemFolha[] = [
        {
            id: 'solicitacao',
            label: 'Solicitações',
            descricao: 'Solicite a importação de extratos bancários.',
            icon: 'pi pi-fw pi-send',
            to: {name: 'solicitacao'},
            visible: temPermissao(EPermissao.CGR_PRISMA_IMPORTACAO_EXTRATO)
        },
        {
            id: 'extrato',
            label: 'Lista Extratos',
            descricao: 'Consulte os extratos bancários importados.',
            icon: 'pi pi-fw pi-list',
            to: {name: 'extrato'},
            visible: temPermissao(EPermissao.CGR_PRISMA_CONSULTAR_EXTRATO)
        },
        {
            id: 'conciliacao',
            label: 'Conciliação',
            descricao: 'Concilie os lançamentos com o extrato bancário.',
            icon: 'pi pi-fw pi-file-edit',
            to: {name: 'conciliacao'},
            visible: temPermissao(EPermissao.CGR_PRISMA_CONSULTAR_CONCILIACAO)
        }
    ];

    // Sem entradas no catálogo EPermissao ainda para Boleto/Convênio — visível para qualquer
    // usuário autenticado até que os códigos reais sejam registrados (ver routes.ts do módulo).
    const financeiroBoletos: MenuItemFolha[] = [
        {
            id: 'boleto-convenio',
            label: 'Convênios',
            descricao: 'Cadastre os convênios bancários (carteira e conta) usados na emissão de boletos.',
            palavrasChave: ['banco', 'carteira'],
            icon: 'pi pi-fw pi-building-columns',
            to: {name: 'boleto-convenio'}
        },
        {
            id: 'boleto',
            label: 'Boletos',
            descricao: 'Emita, consulte e acompanhe boletos de cobrança.',
            palavrasChave: ['cobrança', 'tecnospeed'],
            icon: 'pi pi-fw pi-file',
            to: {name: 'boleto'}
        },
        {
            id: 'boleto-remessa',
            label: 'Arquivo Remessa',
            descricao: 'Gere e acompanhe os arquivos de remessa enviados ao banco.',
            palavrasChave: ['cnab'],
            icon: 'pi pi-fw pi-file-export',
            to: {name: 'boleto-remessa'}
        },
        {
            id: 'boleto-retorno',
            label: 'Arquivo Retorno',
            descricao: 'Envie os arquivos de retorno do banco e confira as liquidações.',
            palavrasChave: ['cnab', 'baixa', 'liquidação'],
            icon: 'pi pi-fw pi-file-import',
            to: {name: 'boleto-retorno'}
        }
    ];

    const financeiroDocumentosFiscais: MenuItemFolha[] = [
        {
            id: 'cte',
            label: 'CT-e - Frete (DF-e)',
            descricao: 'Consulte os CT-e emitidos contra a empresa na SEFAZ e importe-os com finalidade e parcelas.',
            palavrasChave: ['conhecimento de transporte', 'frete', 'xml', 'sefaz','cte','fretes','dfe'],
            icon: 'pi pi-fw pi-truck',
            to: {name: 'cte'},
            visible: temPermissao(EPermissao.CGR_PRISMA_CONSULTAR_DFE_CTE)
        },
        {
            id: 'nfse',
            label: 'NFS-e - Serviço (DF-e)',
            descricao: 'Consulte as NFS-e de serviço tomado, aprove-as e importe-as com finalidade e parcelas.',
            palavrasChave: ['nota fiscal de serviço', 'serviço tomado', 'xml', 'adn', 'nfse', 'aprovação','dfe'],
            icon: 'pi pi-fw pi-briefcase',
            to: {name: 'nfse'},
            visible: false//temPermissao(EPermissao.CGR_PRISMA_CONSULTAR_DFE_NFSE)
        }
    ];

    const compras: MenuItemFolha[] = [
        {
            id: 'requisicao-compra',
            label: 'Requisição de Compra',
            descricao: 'Solicite a compra de itens, anexe documentos e acompanhe a aprovação.',
            palavrasChave: ['solicitação'],
            icon: 'pi pi-fw pi-list-check',
            to: {name: 'requisicao-compra'},
            visible: temPermissao(EPermissao.CGR_PRISMA_CONSULTAR_SOLICITACAO_GERAL) ||
                temPermissao(EPermissao.CGR_PRISMA_CADASTRAR_SOLICITACAO_GERAL) ||
                temPermissao(EPermissao.CGR_PRISMA_APROVACAO_SOLICITACAO_GERAL)
        },
        {
            id: 'pedido-compra',
            label: 'Pedido de Compra',
            descricao: 'Consulte e cadastre pedidos de compra de matéria-prima, insumos e componentes.',
            palavrasChave: ['fornecedor'],
            icon: 'pi pi-fw pi-shopping-bag',
            to: {name: 'pedido-compra'},
            visible: temPermissao(EPermissao.CGR_PRISMA_LISTAR_PEDIDO_COMPRA)
        }
    ];

    // Sem permissão dedicada no catálogo EPermissao (o backend só exige [Authorize]) — visível
    // para qualquer usuário autenticado (ver routes.ts do módulo finalidade).
    const motorTributario: MenuItemFolha[] = [
        {
            id: 'finalidade',
            label: 'Finalidade',
            descricao: 'Cadastre as finalidades (natureza contábil e fiscal) e seus itens.',
            palavrasChave: ['natureza', 'conta contábil'],
            icon: 'pi pi-fw pi-sitemap',
            to: {name: 'finalidade'}
        },
        {
            id: 'matriz-finalidade',
            label: 'Matriz Tributária',
            descricao: 'Relacione CFOP interno, CFOP fora do estado e finalidade.',
            palavrasChave: ['cfop', 'tributação', 'imposto'],
            icon: 'pi pi-fw pi-table',
            to: {name: 'matriz-finalidade'}
        }
    ];

    const artigosEstoque: MenuItemFolha[] = [
        {
            id: 'artigo-classificacao',
            label: 'Classificação de Artigo',
            descricao: 'Cadastre as classificações usadas para agrupar os artigos.',
            icon: 'pi pi-fw pi-tags',
            to: {name: 'artigo-classificacao'},
            visible: temPermissao(EPermissao.CGR_PRISMA_LISTAR_CLASSIFICACAO_ARTIGO)
        },
        {
            id: 'artigo',
            label: 'Artigos',
            descricao: 'Consulte e cadastre artigos e seus parâmetros de estoque.',
            palavrasChave: ['item', 'material'],
            icon: 'pi pi-fw pi-box',
            to: {name: 'artigo'},
            visible: temPermissao(EPermissao.CGR_PRISMA_LISTAR_ARTIGO)
        },
        {
            id: 'artigo-estoque',
            label: 'Gestão de Estoque',
            descricao: 'Controle saldos e movimentações de estoque dos artigos.',
            palavrasChave: ['saldo', 'entrada', 'saída'],
            icon: 'pi pi-fw pi-database',
            to: {name: 'artigo-estoque'},
            visible: temPermissao(EPermissao.CGR_PRISMA_GESTAO_ESTOQUE)
        }
    ];

    const producao: MenuItemFolha[] = [
        {
            id: 'ordem-fabricacao',
            label: 'Ordens de Fabricação',
            descricao: 'Consulte, cadastre e importe ordens de fabricação e suas operações.',
            palavrasChave: ['of', 'ose', 'produção'],
            icon: 'pi pi-fw pi-file-edit',
            to: {name: 'ordem-fabricacao'},
            visible: temPermissao(EPermissao.CGR_PRISMA_GESTAO_ORDEM_FABRICACAO)
        }
    ];

    // TODO(permissão): itens sem `visible` até o usuário informar os códigos de permissão (Playbook 7).
    const administracao: MenuItemFolha[] = [
        {
            id: 'colaborador',
            label: 'Colaboradores',
            descricao: 'Consulte e cadastre colaboradores, e libere o login e o grupo de permissão de acesso.',
            palavrasChave: ['funcionário', 'usuário', 'login', 'senha'],
            icon: 'pi pi-fw pi-users',
            to: {name: 'colaborador'}
        },
        {
            id: 'grupo-permissao',
            label: 'Grupos de Permissão',
            descricao: 'Cadastre os grupos de permissão e marque as funções liberadas para cada um.',
            palavrasChave: ['acesso', 'perfil', 'função'],
            icon: 'pi pi-fw pi-shield',
            to: {name: 'grupo-permissao'}
        }
    ];

    return [
        criarModulo({id: 'inicio', label: 'Início', icon: 'pi pi-fw pi-home', flat: true, subgrupos: [{itens: inicio}]}),
        criarModulo({id: 'operacao', label: 'Operação', icon: 'pi pi-fw pi-list-check', subgrupos: [{itens: operacao}]}),
        criarModulo({id: 'compras', label: 'Compras', icon: 'pi pi-fw pi-shopping-cart', subgrupos: [{itens: compras}]}),
        criarModulo({
            id: 'financeiro',
            label: 'Financeiro',
            icon: 'pi pi-fw pi-wallet',
            subgrupos: [
                {titulo: 'Lançamentos', itens: financeiroLancamentos},
                {titulo: 'Extratos', itens: financeiroExtratos},
                {titulo: 'Boletos', itens: financeiroBoletos},
                {titulo: 'Documentos Fiscais', itens: financeiroDocumentosFiscais}
            ]
        }),
        criarModulo({id: 'artigos-estoque', label: 'Artigos e Estoque', icon: 'pi pi-fw pi-box', subgrupos: [{itens: artigosEstoque}]}),
        criarModulo({
            id: 'relatorios',
            label: 'Relatórios',
            icon: 'pi pi-fw pi-chart-bar',
            subgrupos: [
                {titulo: 'Operacionais', itens: relatoriosOperacionais},
                {titulo: 'Faturamento', itens: relatoriosFaturamento}
            ]
        }),
        criarModulo({id: 'producao', label: 'Produção', icon: 'pi pi-fw pi-cog', subgrupos: [{itens: producao}]}),
        criarModulo({id: 'motor-tributario', label: 'Motor Tributário', icon: 'pi pi-fw pi-percentage', subgrupos: [{itens: motorTributario}]}),
        criarModulo({id: 'administracao', label: 'Administração', icon: 'pi pi-fw pi-users', subgrupos: [{itens: administracao}]})
    ];
}
